<template>
  <div class="fitness-overview">
    <!-- Login Required Message -->
    <div v-if="!isAuthenticated" class="row">
      <div class="col-12">
        <div class="card border-primary">
          <div class="card-body text-center py-5">
            <div class="mb-4">
              <i class="bi bi-person-lock display-1 text-primary"></i>
            </div>
            <h4 class="card-title mb-3">Login Required</h4>
            <p class="card-text text-muted mb-4">
              Please log in to access your personal fitness tracking and get personalized workout recommendations.
            </p>
            <div class="d-flex gap-3 justify-content-center">
              <router-link to="/login" class="btn btn-primary px-4">
                <i class="bi bi-box-arrow-in-right me-2"></i>Login
              </router-link>
              <router-link to="/register" class="btn btn-outline-primary px-4">
                <i class="bi bi-person-plus me-2"></i>Sign Up
              </router-link>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Authenticated Content -->
    <div v-else>
      <div v-if="loading" class="text-center py-5">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
        <p class="text-muted mt-2">Loading fitness data...</p>
      </div>
      
      <!-- Stats Cards -->
    <div v-else class="row g-4 mb-5">
      <div class="col-md-3 col-sm-6">
        <div class="stat-card text-center">
          <div class="stat-icon bg-primary text-white">
            <i class="bi bi-lightning-charge"></i>
          </div>
          <h3 class="stat-number">{{ workoutsThisWeek }}</h3>
          <p class="stat-label text-muted">Workouts This Week</p>
        </div>
      </div>

      <div class="col-md-3 col-sm-6">
        <div class="stat-card text-center">
          <div class="stat-icon bg-success text-white">
            <i class="bi bi-stopwatch"></i>
          </div>
          <h3 class="stat-number">{{ totalMinutes }}</h3>
          <p class="stat-label text-muted">Minutes Exercised</p>
        </div>
      </div>

      <div class="col-md-3 col-sm-6">
        <div class="stat-card text-center">
          <div class="stat-icon bg-warning text-white">
            <i class="bi bi-fire"></i>
          </div>
          <h3 class="stat-number">{{ caloriesBurned }}</h3>
          <p class="stat-label text-muted">Calories Burned</p>
        </div>
      </div>

      <div class="col-md-3 col-sm-6">
        <div class="stat-card text-center">
          <div class="stat-icon bg-info text-white">
            <i class="bi bi-heart-pulse"></i>
          </div>
          <h3 class="stat-number">{{ avgHeartRate }}</h3>
          <p class="stat-label text-muted">Avg Heart Rate</p>
        </div>
      </div>
    </div>

    <!-- Progress Section -->
    <div class="row g-4">
      <div class="col-lg-8">
        <div class="card h-100">
          <div class="card-header">
            <h5 class="mb-0"><i class="bi bi-graph-up me-2"></i>Weekly Progress</h5>
          </div>
          <div class="card-body">
            <div class="progress-chart">
              <div v-if="weeklyProgressData.length === 0" class="text-center text-muted">
                <i class="bi bi-graph-up" style="font-size: 2rem"></i>
                <p class="mt-2">Complete workouts to see your weekly progress</p>
              </div>
              <div v-else class="weekly-chart">
                <div class="chart-container">
                  <div 
                    v-for="(day, index) in weeklyProgressData" 
                    :key="index"
                    class="day-bar-container"
                  >
                    <div class="day-bar-wrapper">
                      <div 
                        class="day-bar" 
                        :style="{ height: day.height + '%' }"
                        :title="`${day.label}: ${day.workouts} workouts, ${day.minutes} minutes`"
                      ></div>
                    </div>
                    <small class="day-label">{{ day.label }}</small>
                  </div>
                </div>
                <div class="chart-legend mt-3">
                  <small class="text-muted">
                    <i class="bi bi-circle-fill text-primary me-1"></i>Daily workout progress
                  </small>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="col-lg-4">
        <div class="card h-100">
          <div class="card-header">
            <h5 class="mb-0"><i class="bi bi-target me-2"></i>Today's Goals</h5>
          </div>
          <div class="card-body">
            <div class="goal-item mb-3">
              <div class="d-flex justify-content-between align-items-center mb-1">
                <span>Steps</span>
                <span>{{ stepsToday }}/10,000</span>
              </div>
              <div class="progress">
                <div
                  class="progress-bar bg-primary"
                  :style="{ width: (stepsToday / 10000) * 100 + '%' }"
                ></div>
              </div>
            </div>

            <div class="goal-item mb-3">
              <div class="d-flex justify-content-between align-items-center mb-1">
                <span>Water</span>
                <span>{{ waterGlasses }}/8 glasses</span>
              </div>
              <div class="progress">
                <div
                  class="progress-bar bg-info"
                  :style="{ width: (waterGlasses / 8) * 100 + '%' }"
                ></div>
              </div>
            </div>

            <div class="goal-item">
              <div class="d-flex justify-content-between align-items-center mb-1">
                <span>Exercise</span>
                <span>{{ exerciseMinutes }}/30 min</span>
              </div>
              <div class="progress">
                <div
                  class="progress-bar bg-success"
                  :style="{ width: (exerciseMinutes / 30) * 100 + '%' }"
                ></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    </div>
  </div>
</template>

<script>
import { useFirebaseAuth } from '@/composables/useFirebaseAuth'
import { ExerciseService } from '@/services/exerciseService'

export default {
  name: 'FitnessOverview',
  setup() {
    const { user, isAuthenticated, initAuth } = useFirebaseAuth()
    return { user, isAuthenticated, initAuth }
  },
  data() {
    return {
      exercises: [],
      exerciseService: null,
      loading: false,
      avgHeartRate: 72,
      stepsToday: 6500,
      waterGlasses: 5,
      exerciseMinutes: 25,
    }
  },

  watch: {
    user: {
      handler(newUser) {
        if (newUser && newUser.uid) {
          this.exerciseService = new ExerciseService(newUser.uid)
          this.loadExercises()
        }
      },
      immediate: true
    }
  },
  computed: {
    completedExercises() {
      return this.exercises.filter(exercise => exercise.completed)
    },

    weeklyStats() {
      const now = new Date()
      const weekStart = new Date(now.setDate(now.getDate() - now.getDay()))
      weekStart.setHours(0, 0, 0, 0)
      
      const thisWeekCompleted = this.completedExercises.filter(exercise => {
        const completedDate = new Date(exercise.completedAt)
        return completedDate >= weekStart
      })

      return {
        workoutsThisWeek: thisWeekCompleted.length,
        totalMinutes: thisWeekCompleted.reduce((sum, ex) => sum + parseInt(ex.duration || 0), 0),
        caloriesBurned: thisWeekCompleted.reduce((sum, ex) => sum + parseInt(ex.calories || 0), 0)
      }
    },

    workoutsThisWeek() {
      return this.weeklyStats.workoutsThisWeek
    },

    totalMinutes() {
      return this.weeklyStats.totalMinutes
    },

    caloriesBurned() {
      return this.weeklyStats.caloriesBurned
    },

    weeklyProgressData() {
      const daysOfWeek = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
      const now = new Date()
      const weekStart = new Date(now.setDate(now.getDate() - now.getDay()))
      weekStart.setHours(0, 0, 0, 0)
      
      const progressData = []
      let maxWorkouts = 0
      
      // Calculate data for each day of the week
      for (let i = 0; i < 7; i++) {
        const dayDate = new Date(weekStart)
        dayDate.setDate(weekStart.getDate() + i)
        dayDate.setHours(23, 59, 59, 999)
        
        const dayWorkouts = this.completedExercises.filter(exercise => {
          const completedDate = new Date(exercise.completedAt)
          const dayStart = new Date(dayDate)
          dayStart.setHours(0, 0, 0, 0)
          return completedDate >= dayStart && completedDate <= dayDate
        })
        
        const dayData = {
          label: daysOfWeek[i],
          workouts: dayWorkouts.length,
          minutes: dayWorkouts.reduce((sum, ex) => sum + parseInt(ex.duration || 0), 0),
          height: 0
        }
        
        progressData.push(dayData)
        maxWorkouts = Math.max(maxWorkouts, dayData.workouts)
      }
      
      // Calculate heights (percentage) based on max workouts
      if (maxWorkouts > 0) {
        progressData.forEach(day => {
          day.height = Math.max((day.workouts / maxWorkouts) * 100, day.workouts > 0 ? 20 : 0)
        })
      }
      
      return progressData // Show all 7 days
    }
  },
  mounted() {
    this.initAuth()
  },
  methods: {
    async loadExercises() {
      if (!this.exerciseService) return
      
      this.loading = true
      try {
        const result = await this.exerciseService.getExercises()
        if (result.success) {
          this.exercises = result.exercises
        } else {
          console.error('Failed to load exercises:', result.error)
        }
      } catch (error) {
        console.error('Failed to load exercises:', error)
      } finally {
        this.loading = false
      }
    }
  },
}
</script>

<style scoped>
.stat-card {
  background: white;
  border-radius: 15px;
  padding: 2rem 1rem;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease;
}

.stat-card:hover {
  transform: translateY(-5px);
}

.stat-icon {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1rem;
  font-size: 1.5rem;
}

.stat-number {
  font-size: 2.5rem;
  font-weight: bold;
  margin-bottom: 0.5rem;
  color: #333;
}

.stat-label {
  font-size: 0.9rem;
  margin: 0;
}

.goal-item .progress {
  height: 8px;
  border-radius: 4px;
}

.progress-chart {
  height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f8f9fa;
  border-radius: 8px;
}

.weekly-chart {
  width: 100%;
  height: 100%;
}

.chart-container {
  display: flex;
  align-items: end;
  justify-content: space-around;
  height: 160px;
  padding: 10px 0;
}

.day-bar-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
  max-width: 60px;
}

.day-bar-wrapper {
  height: 120px;
  display: flex;
  align-items: end;
  width: 100%;
  justify-content: center;
}

.day-bar {
  width: 30px;
  background: linear-gradient(135deg, #007bff, #0056b3);
  border-radius: 4px 4px 0 0;
  min-height: 5px;
  transition: all 0.3s ease;
  cursor: pointer;
}

.day-bar:hover {
  background: linear-gradient(135deg, #0056b3, #004085);
  transform: scaleY(1.05);
}

.day-label {
  margin-top: 8px;
  font-weight: 500;
  color: #6c757d;
}

@media (max-width: 768px) {
  .stat-card {
    padding: 1.5rem 1rem;
  }

  .stat-number {
    font-size: 2rem;
  }

  .stat-icon {
    width: 50px;
    height: 50px;
    font-size: 1.2rem;
  }
}
</style>
